import { accentUtils, from } from "./HelperService";
import { getUser } from "./UserService";
import { queryNoTracking } from './DataService'

const query_GetAddressCountyFromUSCensus = queryNoTracking("Address_CountyFromUSCensus");

const defaultRequiredFields = {
    addressLines: true,
    city: true,
    state: true,
    postcode: true,
    country: true
};

const requiredAddressFields = {
    "US": { ...defaultRequiredFields },
    "GB": { ...defaultRequiredFields },
    "IE": { ...defaultRequiredFields, postcode: false },
    "CA": { ...defaultRequiredFields },
    "ZA": { ...defaultRequiredFields },
    "AU": { ...defaultRequiredFields },
    "NZ": { ...defaultRequiredFields },
    "SG": {  // Singapore required fields
        addressLines: true,
        city: false,
        state: false,
        postcode: true,
        country: true
    }
};

const defaultMapping = {
    AddressLines: [
        {
            name: 'premise',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'subpremise',
            value: 'short_name',
            format: '{0}/'
        },
        {
            name: 'street_number',
            value: 'short_name',
            format: '{0} '
        },
        {
            name: 'route',
            value: 'short_name',
            format: '{0} '
        },
        {
            name: 'neighborhood',
            value: 'short_name',
            format: '{0}'
        },
    ],
    City: [
        {
            name: 'locality',
            value: 'long_name',
        },
        {
            name: 'sublocality',
            value: 'long_name',
        },
        {
            name: 'postal_town',
            value: 'long_name',
        },
    ],
    State: [
        {
            name: 'administrative_area_level_1',
            value: 'short_name',
        },
        {
            name: 'administrative_area_level_2',
            value: 'long_name',
        },
    ],
    StateSubArea1: [
        {
            name: 'administrative_area_level_2',
            value: 'long_name',
        },
    ],
    Postcode: [
        {
            name: 'postal_code',
            value: 'short_name',
        },
    ],
    Country: [
        {
            name: 'country',
            value: 'long_name',
        },
    ]
};

const IrelandMapping = {
    ...defaultMapping,
    State: [
        {
            name: 'administrative_area_level_1',
            value: 'long_name',
        },
        {
            name: 'administrative_area_level_2',
            value: 'long_name',
        },
    ]
};

const USMapping = {
    ...defaultMapping,
    AddressLines: [
        {
            name: 'street_number',
            value: 'short_name',
            format: '{0} '
        },
        {
            name: 'route',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'subpremise',
            value: 'short_name',
            format: 'Apt {0}, '
        },
        {
            name: 'premise',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'neighborhood',
            value: 'short_name',
            format: '{0}'
        },
    ],
    StateSubArea1: [
        {
            name: 'administrative_area_level_2',
            value: 'long_name',
        },
    ]
};

const SouthAfricaMapping = {
    ...defaultMapping,
    AddressLines: [
        {
            name: 'street_number',
            value: 'short_name',
            format: '{0} '
        },
        {
            name: 'route',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'subpremise',
            value: 'short_name',
            format: 'Unit {0}, '
        },
        {
            name: 'premise',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'sublocality_level_1',
            value: 'short_name',
            format: '{0}, '
        },
        {
            name: 'neighborhood',
            value: 'short_name',
            format: '{0}'
        },
    ]
};

const IndiaMapping = {
    ...defaultMapping,
    AddressLines: [
        {
            name: 'subpremise',         // Add this for unit numbers (e.g., Flat 101)
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'premise',            // <<< ADD THIS FOR BUILDING NAME/COMPLEX
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'street_number',
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'route',
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'point_of_interest', // Keep or remove depending on if 'near X' is useful
            value: 'long_name',
            format: 'near {0}, '
        },
        {
            name: 'neighborhood',
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'sublocality_level_3',
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'sublocality_level_2',
            value: 'long_name',
            format: '{0}, '
        },
        {
            name: 'sublocality_level_1',
            value: 'long_name',
            format: '{0}'
        },
    ],
    City: [
        {
            name: 'locality',
            value: 'long_name',
        },
    ],
    State: [
        {
            name: 'administrative_area_level_1',
            value: 'long_name',
        },
    ],
    Postcode: [
        {
            name: 'postal_code',
            value: 'long_name',
        },
    ],
    Country: [
        {
            name: 'country',
            value: 'long_name',
        },
    ]
};

// Singapore Mapping
const SingaporeMapping = {

    AddressLines: [
        {
            name: 'street_number',
            value: 'short_name',
            format: '{0} ' // Block Number
        },
        {
            name: 'route',
            value: 'short_name',
            format: '{0}\n' // Street Name
        },
        {
            name: 'subpremise',
            value: 'short_name',
            format: '#{0}, ' // Floor-Unit (e.g., #30-05)
        },
        {
            name: 'premise',
            value: 'short_name',
            format: '{0}\n' // Building Name (if applicable)
        }
    ],
    City: [],  // Not applicable
    State: [], // Not applicable
    Postcode: [
        {
            name: 'postal_code',
            value: 'short_name'
        }
    ],
    Country: [
        {
            name: 'country',
            value: 'long_name'
        }
    ]
};

// Address model mappings
const defaultMappings = {
    "US": { ...USMapping },
    "GB": { ...defaultMapping },
    "IE": IrelandMapping,
    "CA": { ...defaultMapping },
    "ZA": { ...SouthAfricaMapping },
    "AU": { ...defaultMapping },
    "NZ": { ...defaultMapping },
    "SG": { ...SingaporeMapping }, // Added Singapore mapping
    "IN": { ...IndiaMapping },
};

function customParseStreetNumber(address) {
    if (accentUtils.isEmpty(address)) return {
        unitNumber: null,
        streetNumber: null
    };

    const unitStreetRegex = /(\d+)\/(\d+)/;
    const unitStreetMatch = address.match(unitStreetRegex);

    if (unitStreetMatch) {
        return {
            unitNumber: unitStreetMatch[1],
            streetNumber: unitStreetMatch[2]
        };
    }

    const generalNumberRegex = /^(\d+)/;
    const generalNumberMatch = address.match(generalNumberRegex);

    if (generalNumberMatch) {
        return {
            unitNumber: null,
            streetNumber: generalNumberMatch[1]
        };
    }

    return {
        unitNumber: null,
        streetNumber: null
    };
}

function getAddressComponent(place, type) {
    return place.address_components.find(c => c.types.includes(type));
}

function getComponentValue(component, mappingValue) {
    if (accentUtils.isNull(component)) return null;
    return component[mappingValue.value];
}

export function getRequiredAddressFields(country) {
    const countryCode = getCountryCode(country);
    return requiredAddressFields[countryCode];
}

export function getCountryCode(country) {

    if (accentUtils.isEmpty(country)) {
        country = getUser()?.DefaultSettings?.Country;
    }

    var res = 'AU';

    if (!accentUtils.isEmpty(country)) {
        if (country.toUpperCase() === "NEW ZEALAND") {
            res = 'NZ';
        } else if (country.toUpperCase() === "CANADA") {
            res = 'CA';
        } else if (country.toUpperCase() === "SOUTH AFRICA") {
            res = 'ZA';
        } else if (country.toUpperCase() === "UNITED KINGDOM") {
            res = 'GB';
        } else if (country.toUpperCase() === "UNITED STATES") {
            res = 'US';
        } else if (country.toUpperCase() === "IRELAND") {
            res = 'IE';
        } else if (country.toUpperCase() === "INDIA") {
            res = 'IN';
        } else if (country.toUpperCase() === "SINGAPORE") {
            res = 'SG';
        }
    }

    return res;
}

export async function getAddressFromPlace(place, addressText) {
    const country = getAddressComponent(place, "country")["long_name"];
    const countryCode = getCountryCode(country);

    const mapping = defaultMappings[countryCode];
    let addressModel = {
        AddressLines: '',
        City: '',
        StateSubArea1: '',
        State: '',
        Postcode: '',
        Country: '',
        PosLong: place.geometry.location.lng(),
        PosLat: place.geometry.location.lat()
    };

    const addressNumbers = customParseStreetNumber(addressText);

    addressModel.Country = from(mapping.Country)
        .select(c => getComponentValue(getAddressComponent(place, c.name), c))
        .where(v => !accentUtils.isEmpty(v))
        .firstOrDefault();
    addressModel.State = from(mapping.State)
        .select(c => getComponentValue(getAddressComponent(place, c.name), c))
        .where(v => !accentUtils.isEmpty(v) && v !== addressModel.Country)
        .firstOrDefault();
    
    // Get StateSubArea1 (County) from Google Places for USA
    if (countryCode === "US" && mapping.StateSubArea1) {
        addressModel.StateSubArea1 = from(mapping.StateSubArea1)
            .select(c => getComponentValue(getAddressComponent(place, c.name), c))
            .where(v => !accentUtils.isEmpty(v))
            .firstOrDefault();

        // Fallback to US Census API if Google Places didn't return StateSubArea1
        if (accentUtils.isEmpty(addressModel.StateSubArea1) && !accentUtils.isEmpty(addressText)) {
            addressModel.StateSubArea1 =
                await query_GetAddressCountyFromUSCensus.getFirstOrDefault({ addressText: addressText });
        }
    }
    
    addressModel.City = from(mapping.City)
        .select(c => getComponentValue(getAddressComponent(place, c.name), c))
        .where(v => !accentUtils.isEmpty(v) && v !== addressModel.State && v !== addressModel.Country)
        .firstOrDefault();
    addressModel.Postcode = from(mapping.Postcode)
        .select(c => getComponentValue(getAddressComponent(place, c.name), c))
        .where(v => !accentUtils.isEmpty(v))
        .firstOrDefault();

    addressModel.AddressLines = from(mapping.AddressLines)
        .select(c => {
            const isUnit = c.name === "subpremise";
            const hasCustomParseUnit = !accentUtils.isEmpty(addressNumbers.unitNumber);
            const isStreetNumber = c.name === "street_number";
            const hasCustomParseStreetNumber = !accentUtils.isEmpty(addressNumbers.streetNumber);

            const component = getAddressComponent(place, c.name);
            var value = getComponentValue(component, c);

            if (accentUtils.isEmpty(value)) {
                if (isUnit && hasCustomParseUnit) {
                    return c.format.format(addressNumbers.unitNumber);
                }
                if (isStreetNumber && hasCustomParseStreetNumber) {
                    return c.format.format(addressNumbers.streetNumber);
                }
                return null;
            }

            return c.format.format(value);
        })
        .where(v => !accentUtils.isEmpty(v))
        .toArray()
        .join("");

    let finalAddressLines = addressModel.AddressLines.trim(); // Start with the component lines


    const allowPrependName = countryCode === "IN";

    // Logic to potentially prepend place.name or its parts
    if (allowPrependName && place.name && place.name.trim() !== '') {
        const placeNameTrimmed = place.name.trim();
        const componentLinesTrimmed = finalAddressLines; // Use the current final value

        // More robust cleaning function
        const cleanPart = (part) => {
            if (!part) return '';
            return part.replace(/^[^\w]+|[^\w]+$/g, '').trim(); // Remove leading/trailing non-alphanumeric chars
        };

        const normalize = (str) => cleanPart(str).toLowerCase();
        const normPlaceName = normalize(placeNameTrimmed);
        const normComponentLines = normalize(componentLinesTrimmed);

        if (!normComponentLines.startsWith(normPlaceName)) {
            let firstComponentValueInPlaceName = null;
            let firstComponentValueIndex = -1;

            // Find the first component value (from mapping) that appears in componentLines
            for (const componentMapping of mapping.AddressLines) {
                const component = getAddressComponent(place, componentMapping.name);
                let value = getComponentValue(component, componentMapping);

                // Prioritize custom parsed numbers if component value is missing
                if (accentUtils.isEmpty(value)) {
                     if (componentMapping.name === 'street_number' && addressNumbers.streetNumber) {
                         value = addressNumbers.streetNumber;
                     } else if (componentMapping.name === 'subpremise' && addressNumbers.unitNumber) {
                         value = addressNumbers.unitNumber;
                     }
                }

                if (!accentUtils.isEmpty(value)){
                    const valueClean = cleanPart(value);
                    const componentLinesForCheck = cleanPart(componentLinesTrimmed);
                    const placeNameForCheck = cleanPart(placeNameTrimmed);

                    // Check if cleaned value is in cleaned component lines
                     if (componentLinesForCheck.includes(valueClean)) {
                        // Check if cleaned value exists in cleaned place.name
                        const indexInPlaceName = placeNameForCheck.indexOf(valueClean);
                        if (indexInPlaceName !== -1) {
                            // Use the *original* value (not cleaned) here for substring splitting
                            firstComponentValueInPlaceName = value; 
                            // Find index in *original* placeNameTrimmed for accurate splitting
                            firstComponentValueIndex = placeNameTrimmed.indexOf(value);
                            if (firstComponentValueIndex !== -1) break; // Found first match
                            else { // Fallback if original value string differs too much
                                firstComponentValueIndex = indexInPlaceName; // Use index from cleaned check
                                break;
                            }
                        }
                    }
                }
            }

            // --- Decision Logic --- based on findings

            if (firstComponentValueInPlaceName && firstComponentValueIndex > 0) {
                // Case 1: Component found, but not at the start of place.name
                let prefix = cleanPart(placeNameTrimmed.substring(0, firstComponentValueIndex));
                if (prefix) {
                    finalAddressLines = [prefix, componentLinesTrimmed].join(', ');
                } else {
                    // Prefix was empty/non-alphanumeric, check full name inclusion
                    if (!normComponentLines.includes(normPlaceName)) {
                        finalAddressLines = [placeNameTrimmed, componentLinesTrimmed].join(', ');
                    }
                    // else: Already included or starts with, finalAddressLines remains componentLinesTrimmed
                }
            } else if (firstComponentValueInPlaceName && firstComponentValueIndex === 0) {
                // Case 2: Component found at the start of place.name
                const firstPartClean = cleanPart(firstComponentValueInPlaceName);
                const placeRemainderClean = cleanPart(placeNameTrimmed.substring(firstComponentValueInPlaceName.length));
                const componentRemainderClean = cleanPart(componentLinesTrimmed.substring(firstComponentValueInPlaceName.length));

                if (placeRemainderClean && !componentRemainderClean.toLowerCase().includes(placeRemainderClean.toLowerCase())) {
                    // Combine the distinct parts
                    let partsToJoin = [firstPartClean, placeRemainderClean, componentRemainderClean];
                    finalAddressLines = partsToJoin.filter(p => p).join(', '); // Filter truthy, join
                } else {
                    // Remainder is empty or already included, use original component lines
                    // finalAddressLines remains componentLinesTrimmed
                }

            } else {
                // Case 3: No common component found, or first component index was -1.
                // Prepend full place.name only if it's not already contained.
                if (!normComponentLines.includes(normPlaceName)) {
                     finalAddressLines = [placeNameTrimmed, componentLinesTrimmed].join(', ');
                }
                 // else: Already included, finalAddressLines remains componentLinesTrimmed
            }
        }
         // else: Case 0: Component lines already start with place name, finalAddressLines remains componentLinesTrimmed
    }
    // else: place.name is empty, finalAddressLines remains componentLinesTrimmed

    // Assign the final calculated string ONCE
    addressModel.AddressLines = finalAddressLines;

    return addressModel;
}
